import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from 'src/app/auth/auth.service';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { LocalStorageKeys } from 'src/app/core/enums/LocalStorageKeys';
import { RoleFilterSelected } from 'src/app/shared/role-filter-dropdown-row/role-filter-dropdown-row.component';
import { RoleFilterIds, RoleFilterService } from 'src/app/services/role-filter.service';

export interface ActiveRouteServiceResponse {
  Page: number;
  PageCount: number;
  Services: ActiveRouteService[];
}

export interface ActiveRouteService {
  OrderHash: string;
  ProductTypes: number[];
  OriginAddress: string;
  DestinationAddress: string;
  LicensePlate: string;
  Brand: string;
  Model: string;
  ChassisType: number;
  FuelType: number;
  DriverName: string;
  DriverLatitude: number;
  DriverLongitude: number;
  OrderStatus: number;
}


const MockActiveRouteServicesResponse: ActiveRouteServiceResponse = {
  "Page": 1,
  "PageCount": 1,
  "Services": [
    {
      "OrderHash": "B-FFBAC71A",
      "ProductTypes": [
        6
      ],
      "OriginAddress": "Unit 4, Granda Trading Estate, (Off) Park Street, B69 4LH",
      "DestinationAddress": "Staverton Garage, Gloucester Road, GL51 0TF",
      "LicensePlate": "BL22HHU",
      "Brand": "FORD",
      "Model": "TRANSIT LUTON",
      "ChassisType": 1,
      "FuelType": 3,
      "DriverName": "",
      "DriverLatitude": 51.901016,
      "DriverLongitude": -2.15055,
      "OrderStatus": 5
    },
    {
      "OrderHash": "B-4D0A2C4D",
      "ProductTypes": [
        6
      ],
      "OriginAddress": "23 Broadwater Road, AL7 3BQ",
      "DestinationAddress": "Bruntingthorpe, Lutterworth LE17 5QS, UK",
      "LicensePlate": "LG74GCE",
      "Brand": "MG",
      "Model": "MG 3",
      "ChassisType": 1,
      "FuelType": 5,
      "DriverName": "",
      "DriverLatitude": 51.7993353,
      "DriverLongitude": -0.1996052,
      "OrderStatus": 3
    }
  ]
}


@Injectable({
  providedIn: 'root',
})
export class ActiveRoutesService {
  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private LocalStorageService: LocalStorageService,
    private RoleFilterService: RoleFilterService
  ) { }

  baseUrl = environment.tracking_url;

  // getAuthHeaders(): Observable<{ Authorization: string }> {
  //   return from(this.authService.getAccessToken()).pipe(
  //     map((token) => ({ Authorization: 'Bearer ' + token })),
  //     catchError((error) => {
  //       console.error('Error fetching token', error);
  //       throw error;
  //     })
  //   );
  // }

  activeRoutesMap(page: number, licensePlate: string, RoleFilter: RoleFilterSelected): Observable<ActiveRouteServiceResponse> {
    const RoleFilterIds: RoleFilterIds = this.RoleFilterService.convertRoleFilterSelected_To_RoleFilterIds(RoleFilter)

    // if (environment.useMockData) {
    //   return of(MockActiveRouteServicesResponse);
    // }
    let url = `${this.baseUrl}/fleet-manager/active-routes/map?page=${page}`;
    if (licensePlate) {
      url += `&licensePlate=${licensePlate}`;
    }
    return this.http.post<ActiveRouteServiceResponse>(url, RoleFilterIds)
    // let url = `${this.baseUrl}/fleet-manager/active-routes/map?page=${page}`;
    // if (licensePlate) {
    //   url += `&licensePlate=${licensePlate}`;
    // }
    // return this.getAuthHeaders().pipe(switchMap((headers) => this.http.get<ActiveRouteServiceResponse>(url, { headers })));
  }

  activeRoutesList(page: number, licensePlate: string, RoleFilter: RoleFilterSelected): Observable<any> {
    const RoleFilterIds: RoleFilterIds = this.RoleFilterService.convertRoleFilterSelected_To_RoleFilterIds(RoleFilter)

    let url = `${this.baseUrl}/fleet-manager/active-routes/list?page=${page}`;
    if (licensePlate) {
      url += `&licensePlate=${licensePlate}`;
    }
    return this.http.post(url, RoleFilterIds)
    // let url = `${this.baseUrl}/fleet-manager/active-routes/list?page=${page}`;
    // if (licensePlate) {
    //   url += `&licensePlate=${licensePlate}`;
    // }
    // return this.getAuthHeaders().pipe(switchMap((headers) => this.http.get(url, { headers })));
  }

  activeRoutesDetails(orderHash: string): Observable<any> {
    const url = `${this.baseUrl}/fleet-manager/services/${orderHash}/details`;
    return this.http.get(url);
    // const url = `${this.baseUrl}/fleet-manager/services/${orderHash}/details`;
    // return this.getAuthHeaders().pipe(switchMap((headers) => this.http.get(url, { headers })));
  }


  addOrderPdf(orderHash: string, pdfId: string, clientId?: string): Observable<any> {
    let params = new HttpParams()
      .set('pdfId', pdfId);

    if (clientId) {
      params = params.set('clientId', clientId);
    }

    const url = `${this.baseUrl}/service/${orderHash}/add-order-pdf`;
    return this.http.post(url, {}, { params });
  }

  removeOrderPdf(orderHash: string, pdfId: string): Observable<any> {
    const url = `${this.baseUrl}/service/${orderHash}/remove-order-pdf?pdfId=${pdfId}`;
    return this.http.delete(url);
  }

  getChecklistResult(orderHash: string): Observable<any> {
    const savedLanguage = this.LocalStorageService.get<string>(LocalStorageKeys.Language) || 'es';

    let checklistLanguage;
    switch (savedLanguage) {
      case 'es':
        checklistLanguage = 'es-ES';
        break;
      case 'en':
        checklistLanguage = 'en-GB';
        break;
      case 'fr':
        checklistLanguage = 'fr-Fr';
        break;
      default:
        checklistLanguage = 'es-ES';
    }

    const url = `${this.baseUrl}/tracking/checklist?lang=${checklistLanguage}&caflerHash=${orderHash}`;
    return this.http.get(url)
    // const url = `${this.baseUrl}/tracking/checklist?lang=${checklistLanguage}&caflerHash=${orderHash}`;
    // return this.getAuthHeaders().pipe(switchMap((headers) => this.http.get(url, { headers })));
  }
}
